import { KoobLocationSearch, SEARCH_TYPES } from '@koob/search';
import { getSegments } from 'api/node/segments';
import { getThemes } from 'api/node/themes';
import LocationAwareSearch from 'components/LocationAwareSearch';
import { useField, useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import { KoobInputSelect, KoobInputSelectField } from '@koob/ui';
import { useApp, useSearchParams } from '../../../hooks';
import { getExperiencesList } from './Compatibility';
import { Spinner } from '@koob/margaret';

const LOCATION_SEARCH_TYPES = [SEARCH_TYPES.PROGRAM];

export default function CompatibilitySearchPanel({
  experienceId,
  experience,
  currentStep,
  activeKind,
  assignedIds,
  onAssign,
  isEditable,
}) {
  const { t, i18n } = useTranslation('experiences');
  const { currentOrganizationId } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const requiredOptions =
    experience.extras
      ?.flatMap((extra) => extra.recommendations || [])
      .map((rec) => rec.id) ?? [];

  const { values, setFieldValue } = useFormikContext();

  const [{ value: themes = [] }] = useField('themes');
  const [{ value: segments = [] }] = useField('segments');

  const [state, setState] = useState(null);
  const stateOptions = [
    { value: null, label: t('steps.compatibility.search.all') },
    { value: 'available', label: t('state.available') },
    { value: 'stand_by', label: t('state.standBy') },
    { value: 'in_progress', label: t('state.inProgress') },
  ];
  const [location, setLocation] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const stepConfig = useMemo(() => {
    switch (currentStep) {
      case 1:
        return {
          startingPoint: experience.endPosition || '',
          endingPoint: '',
          notStartingAt: null,
          notEndingAt: null,
          hint: t('compatibility.hints.step1'),
        };
      case 2:
        return {
          startingPoint: experience.endPosition
            ? t('compatibility.notEqual', { city: experience.endPosition })
            : '',
          endingPoint: '',
          notStartingAt: experience.endPosition || null,
          notEndingAt: null,
          hint: t('compatibility.hints.step2'),
        };
      case 3:
        return {
          startingPoint: '',
          endingPoint: experience.startPosition || '',
          notStartingAt: null,
          notEndingAt: null,
          hint: t('compatibility.hints.step3'),
        };
      case 4:
        return {
          startingPoint: '',
          endingPoint: experience.startPosition
            ? t('compatibility.notEqual', { city: experience.startPosition })
            : '',
          notStartingAt: null,
          notEndingAt: experience.startPosition || null,
          hint: t('compatibility.hints.step4'),
        };
      default:
        return {
          startingPoint: '',
          endingPoint: '',
          notStartingAt: null,
          notEndingAt: null,
          hint: '',
        };
    }
  }, [currentStep, experience, t]);

  useEffect(() => {
    if (!experience?.id) return;

    setSearchParams({
      ...searchParams,
      startingPoint: stepConfig.startingPoint,
      endingPoint: stepConfig.endingPoint,
    });

    if (values.startingPoint !== stepConfig.startingPoint) {
      setFieldValue('startingPoint', stepConfig.startingPoint);
    }
    if (values.endingPoint !== stepConfig.endingPoint) {
      setFieldValue('endingPoint', stepConfig.endingPoint);
    }

    setSelectedIds(new Set());
  }, [currentStep, experience?.id, stepConfig]);

  const urlStartingPoint = searchParams?.startingPoint || '';
  const urlEndingPoint = searchParams?.endingPoint || '';

  const apiStartingPoint =
    currentStep === 2 && urlStartingPoint === stepConfig.startingPoint
      ? ''
      : urlStartingPoint;

  const apiEndingPoint =
    (currentStep === 4 || currentStep === 2) &&
    urlEndingPoint === stepConfig.endingPoint
      ? ''
      : urlEndingPoint;
  const experienceFilters = {
    except: experienceId,
    search: location?.kind === 'experience' ? location?.title || '' : '',
    cities: location?.kind === 'city' && location?.id ? [location.id] : [],
    countries:
      location?.kind === 'country' && location?.id ? [location.id] : [],

    startingPoint: apiStartingPoint,
    endingPoint: apiEndingPoint,
    notStartingAt: stepConfig.notStartingAt,
    notEndingAt: stepConfig.notEndingAt,

    state: state?.value,
    themes: themes.map((t) => t.id),
    segments: segments.map((s) => s.id),
  };

  const { data, isLoading } = useAsync({
    promiseFn: getExperiencesList,
    ...experienceFilters,
    watchFn: (props, prevProps) => {
      return (
        !isEqual(props.type, prevProps.type) ||
        !isEqual(props.search, prevProps.search) ||
        !isEqual(props.state, prevProps.state) ||
        !isEqual(props.themes, prevProps.themes) ||
        !isEqual(props.countries, prevProps.countries) ||
        !isEqual(props.segments, prevProps.segments) ||
        !isEqual(props.cities, prevProps.cities) ||
        props.startingPoint !== prevProps.startingPoint ||
        props.endingPoint !== prevProps.endingPoint ||
        props.notStartingAt !== prevProps.notStartingAt ||
        props.notEndingAt !== prevProps.notEndingAt
      );
    },
  });

  const requiredOptionsSet = useMemo(
    () => new Set(requiredOptions),
    [requiredOptions],
  );

  const results = useMemo(() => data?.data || [], [data]);
  const visibleResults =
    currentStep === 2 || currentStep === 4
      ? results.filter((e) => !requiredOptionsSet.has(e.id))
      : results;
  const assignableResults =
    currentStep === 0
      ? visibleResults
      : visibleResults.filter((e) => !assignedIds.has(e.id));
  const alreadyAssignedResults =
    currentStep === 0
      ? []
      : visibleResults.filter((e) => assignedIds.has(e.id));
  const allSelected =
    assignableResults.length > 0 &&
    assignableResults.every((e) => selectedIds.has(e.id));

  const toggleSelectAll = () =>
    setSelectedIds(
      allSelected ? new Set() : new Set(assignableResults.map((e) => e.id)),
    );

  const clearSelection = () => setSelectedIds(new Set());

  return (
    <div
      className={`bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col h-[650px] ${
        !isEditable ? 'opacity-50 pointer-events-none' : ''
      }`}
    >
      <div className="flex items-center gap-2 mb-0.5 text-orange-500 font-semibold text-sm">
        <i className="fa-light fa-magnifying-glass"></i>
        <span>{t('search.title')}</span>
        {isLoading && <Spinner size={15} />}
      </div>
      <p className="text-xs text-slate-400 mb-4">{t('search.subtitle')}</p>

      <div className="space-y-2 mb-1">
        <KoobLocationSearch
          placeholder={t('compatibility.search.locationPlaceholder')}
          types={LOCATION_SEARCH_TYPES}
          locale={i18n.language}
          // The experience's owner, not the chosen view (a group owns nothing).
          fromOrganizationId={
            experience?.organizationId ?? currentOrganizationId
          }
          value={location}
          onValueChange={setLocation}
          kinds={['experience']}
          excludedExperienceIds={[experienceId]}
        />

        <div className="grid grid-cols-2 gap-2">
          <LocationAwareSearch
            name="startingPoint"
            placeholder={t('compatibility.search.startingPoint')}
            disabled={!isEditable}
            defaultValue={stepConfig.startingPoint || ''}
          />

          <LocationAwareSearch
            name="endingPoint"
            placeholder={t('compatibility.search.endingPoint')}
            disabled={!isEditable}
            defaultValue={stepConfig.endingPoint || ''}
          />
        </div>

        {stepConfig.hint && (
          <p className="text-[11px] text-orange-500 italic leading-snug">
            *{stepConfig.hint}
          </p>
        )}
      </div>

      <div
        onClick={() => isEditable && setShowFilters(!showFilters)}
        className="text-xs text-slate-500 font-medium flex items-center gap-1 mb-3 mt-2 cursor-pointer hover:text-slate-700"
      >
        <i className="fa-light fa-bars-filter"></i>{' '}
        {showFilters ? t('filters.hide') : t('filters.show')}
      </div>

      {showFilters && (
        <div className="space-y-3 mb-3 p-2 bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
          <div>
            <div className="text-black font-medium">{t('state.title')}</div>
            <KoobInputSelect
              value={state?.value}
              onValueChange={(value) =>
                setState(stateOptions.find((option) => option.value === value))
              }
              options={stateOptions}
              placeholder={t('steps.compatibility.search.all')}
            />
          </div>

          <KoobInputSelectField
            label={t('form.segments')}
            name="segments"
            multiple
            query={getSegments}
            renderOption={({ displayName }) => displayName}
            renderValue={({ displayName }) => displayName}
          />

          <KoobInputSelectField
            label={t('form.themes')}
            name="themes"
            multiple
            query={getThemes}
            renderOption={({ displayName }) => displayName}
            renderValue={({ displayName }) => displayName}
          />
        </div>
      )}

      <div className="border-t border-slate-100 my-2 pt-2 flex justify-between items-center text-xs text-slate-500">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            disabled={!isEditable}
            className="rounded text-orange-500 shadow-sm focus:ring-orange-500"
          />
          <span className="font-medium text-slate-700">
            {selectedIds.size > 0
              ? t('search.selectedCount', { count: selectedIds.size })
              : t('search.selectAll')}
          </span>
        </label>
        {selectedIds.size > 0 && (
          <span
            onClick={clearSelection}
            className="text-xs text-slate-400 hover:text-slate-600 underline cursor-pointer"
          >
            {t('search.clear')}
          </span>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div className="flex justify-start mb-3">
          <button
            type="button"
            onClick={() => {
              onAssign(visibleResults.filter((e) => selectedIds.has(e.id)));
              setSelectedIds(new Set());
            }}
            className={`px-3 py-1.5 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 shadow-sm transition border-none ${
              activeKind === 'best-match'
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            <i
              className={`fa-light ${
                activeKind === 'best-match'
                  ? 'fa-circle-plus'
                  : 'fa-circle-xmark'
              }`}
            ></i>
            {activeKind === 'best-match'
              ? t('search.addBest')
              : t('search.addBlack')}
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-2 mt-1">
        {assignableResults.map((exp) => (
          <div
            key={exp.id}
            className={`border rounded-xl p-3 flex items-start space-x-3 bg-white cursor-pointer transition ${
              selectedIds.has(exp.id)
                ? 'border-orange-300 bg-orange-50/30'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => {
              setSelectedIds((prev) => {
                const next = new Set(prev);
                next.has(exp.id) ? next.delete(exp.id) : next.add(exp.id);
                return next;
              });
            }}
          >
            <input
              type="checkbox"
              checked={selectedIds.has(exp.id)}
              readOnly
              className="mt-0.5 rounded text-orange-500 pointer-events-none"
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-xs text-slate-700 truncate">
                {exp.name}
              </h4>
              <p className="text-[11px] text-slate-400 mt-0.5">
                <i className="fa-light fa-location-dot"></i>{' '}
                {exp.startPosition?.name || exp.inCity?.title} →{' '}
                {exp.endPosition?.name || exp.inCity?.title}
              </p>
              {exp.supplier?.name && (
                <p className="text-[10px] text-slate-300 uppercase tracking-wider mt-0.5 font-medium">
                  {exp.supplier.name}
                </p>
              )}
            </div>
          </div>
        ))}

        {alreadyAssignedResults.map((exp) => (
          <div
            key={exp.id}
            className="border border-slate-100 rounded-xl p-3 flex items-start space-x-3 bg-slate-50/50 opacity-70"
          >
            <input
              type="checkbox"
              disabled
              checked={false}
              readOnly
              className="mt-0.5 rounded text-slate-300"
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-xs text-slate-400 truncate">
                {exp.name}
              </h4>
              <p className="text-[11px] text-slate-300 mt-0.5">
                <i className="fa-light fa-location-dot"></i>{' '}
                {exp.startPosition?.name || exp.inCity?.title} →{' '}
                {exp.endPosition?.name || exp.inCity?.title}
              </p>
              {exp.supplier?.name && (
                <p className="text-[10px] text-slate-300 uppercase tracking-wider mt-0.5 font-medium">
                  {exp.supplier.name}
                </p>
              )}
              <span className="text-[10px] text-orange-400 font-medium mt-0.5 block">
                {t('search.alreadyAssigned')}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
